import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Send, Building2, User, Mail, MessageSquare, Loader2, Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    facility: "",
    email: "",
    message: "",
    request_demo: false,
  });

  useEffect(() => {
    // Pre-select demo checkbox if navigated from Schedule Demo
    if (location.state?.requestDemo) {
      setFormData(prev => ({ ...prev, request_demo: true }));
    }
  }, [location]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({ ...prev, request_demo: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data) {
        toast.success("Message sent successfully!", {
          description: formData.request_demo 
            ? "We'll reach out shortly to schedule your demo." 
            : "We'll get back to you soon.",
        });
        
        // Reset form
        setFormData({
          name: "",
          facility: "",
          email: "",
          message: "",
          request_demo: false,
        });
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Failed to send message", {
        description: "Please try again or email us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScheduleDemo = () => {
    setFormData(prev => ({ ...prev, request_demo: true }));
  };

  const handleContactUs = () => {
    // Already on contact page
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[#0A0A0A]">
      <Header onScheduleDemo={handleScheduleDemo} onContactUs={handleContactUs} />
      
      <main className="pt-20 sm:pt-24 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          {/* Header */}
          <div className="text-center mb-10 sm:mb-16">
            <span className="text-xs sm:text-sm font-medium uppercase tracking-widest text-cyan-500 mb-3 sm:mb-4 block">
              Get In Touch
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
              Contact Us
            </h1>
            <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto px-4">
              Ready to transform your documentation workflow? We'd love to hear from you.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-20">
            {/* Contact Form */}
            <div className="glass-card p-8 md:p-10">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                {/* Name Field */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-300 flex items-center gap-2">
                    <User className="w-4 h-4 text-cyan-500" />
                    Name
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Dr. John Smith"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    data-testid="contact-name-input"
                    className="bg-white/5 border-white/10 focus:border-cyan-500/50 h-12 text-white placeholder:text-slate-500"
                  />
                </div>

                {/* Facility Field */}
                <div className="space-y-2">
                  <Label htmlFor="facility" className="text-slate-300 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-cyan-500" />
                    Facility / Organization
                  </Label>
                  <Input
                    id="facility"
                    name="facility"
                    type="text"
                    placeholder="ABC Medical Center"
                    value={formData.facility}
                    onChange={handleInputChange}
                    required
                    data-testid="contact-facility-input"
                    className="bg-white/5 border-white/10 focus:border-cyan-500/50 h-12 text-white placeholder:text-slate-500"
                  />
                </div>

                {/* Email Field */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-cyan-500" />
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="doctor@facility.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    data-testid="contact-email-input"
                    className="bg-white/5 border-white/10 focus:border-cyan-500/50 h-12 text-white placeholder:text-slate-500"
                  />
                </div>

                {/* Message Field */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-slate-300 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-cyan-500" />
                    Message
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Tell us about your documentation needs..."
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    data-testid="contact-message-input"
                    className="bg-white/5 border-white/10 focus:border-cyan-500/50 text-white placeholder:text-slate-500 resize-none"
                  />
                </div>

                {/* Demo Checkbox */}
                <div className="flex items-center space-x-3 py-2">
                  <Checkbox
                    id="request_demo"
                    checked={formData.request_demo}
                    onCheckedChange={handleCheckboxChange}
                    data-testid="contact-demo-checkbox"
                    className="border-white/20 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                  />
                  <Label 
                    htmlFor="request_demo" 
                    className="text-slate-300 cursor-pointer text-sm"
                  >
                    I'd like to schedule a demo
                  </Label>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-btn"
                  className="w-full h-14 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold rounded-full transition-all shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_30px_rgba(6,182,212,0.5)]"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Info Panel */}
            <div className="space-y-6 sm:space-y-8">
              {/* Contact Info */}
              <div className="glass-card p-6 sm:p-8">
                <h3 className="text-xl font-semibold text-white mb-4">
                  Get in Touch Directly
                </h3>
                <a 
                  href="tel:+12267598959" 
                  className="flex items-center gap-3 text-cyan-400 hover:text-cyan-300 transition-colors text-lg"
                  data-testid="contact-phone-link"
                >
                  <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                    <Phone className="w-5 h-5" />
                  </div>
                  <span>+1 (226) 759-8959</span>
                </a>
              </div>

              <div className="glass-card p-6 sm:p-8">
                <h3 className="text-xl font-semibold text-white mb-4">
                  Why Choose MedinoteX?
                </h3>
                <ul className="space-y-4">
                  {[
                    "24-hour turnaround on documentation",
                    "HIPAA-compliant workflows",
                    "Trained medical scribes",
                    "AI-enhanced accuracy",
                    "Seamless EHR integration",
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 rounded-full bg-cyan-500" />
                      </div>
                      <span className="text-slate-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="glass-card p-8">
                <h3 className="text-xl font-semibold text-white mb-4">
                  What Happens Next?
                </h3>
                <div className="space-y-4">
                  {[
                    { step: "1", text: "We'll review your inquiry within 24 hours" },
                    { step: "2", text: "Our team will reach out to discuss your needs" },
                    { step: "3", text: "We'll customize a solution for your facility" },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-cyan-500 font-semibold text-sm">{item.step}</span>
                      </div>
                      <span className="text-slate-300 pt-1">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap gap-3">
                <div className="trust-badge">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  HIPAA Compliant
                </div>
                <div className="trust-badge">
                  <div className="w-2 h-2 rounded-full bg-cyan-500" />
                  256-bit Encryption
                </div>
                <div className="trust-badge">
                  <div className="w-2 h-2 rounded-full bg-cyan-500" />
                  SOC 2 Type II
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
